import { computed, inject } from '@angular/core';
import { Component, input, output } from '@angular/core';
import { MDContentBoxComponent, MDIconComponent } from '@melodic-ui/components';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { RescheduleLessonDialogService } from '@lb/services/reschedule-lesson-dialog.service';
import { take } from 'rxjs';
import { LESSON_STATUS_CONFIG } from '@lb/components/lessons/utils/lesson-status-config';
import { isLessonEditable } from '@lb/components/lessons/utils/lesson-helper.util';
import { IndicatorType } from '@melodic-ui/types';

export type LessonDetailUnavailableReason = 'absent' | 'awaiting-payment' | 'pending-reschedule' | 'not-yet-taught' | 'awaiting-details';

interface IUnavailableConfig {
	icon: string;
	title: string;
	message: string;
}

const UNAVAILABLE_CONFIG: Record<LessonDetailUnavailableReason, Record<'instructor' | 'learner', IUnavailableConfig>> = {
	'absent': {
		instructor: {
			icon: LESSON_STATUS_CONFIG['absent'].icon,
			title: 'Lesson Absent',
			message: 'This lesson was marked as absent. No lesson details are available.'
		},
		learner: {
			icon: LESSON_STATUS_CONFIG['absent'].icon,
			title: 'Lesson Absent',
			message: 'This lesson was marked as absent. No lesson details are available.'
		}
	},
	'awaiting-payment': {
		instructor: {
			icon: LESSON_STATUS_CONFIG['awaiting-payment'].icon,
			title: 'Awaiting Payment',
			message: 'This lesson is awaiting payment. Lesson details will be available once payment has been received.'
		},
		learner: {
			icon: LESSON_STATUS_CONFIG['awaiting-payment'].icon,
			title: 'Awaiting Payment',
			message: 'This lesson is awaiting payment. Please contact your account manager to resolve the outstanding balance.'
		}
	},
	'pending-reschedule': {
		instructor: {
			icon: LESSON_STATUS_CONFIG['reschedule'].icon,
			title: 'Pending Reschedule',
			message: 'This lesson is pending a reschedule. Please confirm a new lesson time with the learner to continue.'
		},
		learner: {
			icon: LESSON_STATUS_CONFIG['reschedule'].icon,
			title: 'Pending Reschedule',
			message: 'This lesson has been requested for rescheduling. Details will be available once a new time is confirmed.'
		}
	},
	// An instructor may fill a lesson in ahead of time and save it as a draft. That content is theirs
	// until the lesson is taught, so the learner sees this instead of a half-written write-up. The
	// instructor copy exists only to satisfy the config shape — instructors are never shown this.
	'not-yet-taught': {
		instructor: {
			icon: LESSON_STATUS_CONFIG['scheduled'].icon,
			title: 'Lesson Not Yet Taught',
			message: 'This lesson has not been taught yet.'
		},
		learner: {
			icon: LESSON_STATUS_CONFIG['scheduled'].icon,
			title: 'Lesson Not Yet Taught',
			message: 'Lesson details will be available once your instructor has taught and completed this lesson.'
		}
	},
	// Same gate as not-yet-taught, but the lesson's time has already passed, so "not yet taught" reads
	// as wrong to the learner who just sat through it. No lesson status matches this state (the lesson
	// is still Scheduled), hence the literal icon rather than one from LESSON_STATUS_CONFIG.
	'awaiting-details': {
		instructor: {
			icon: 'untitled-ui/time/hourglass-01',
			title: 'Awaiting Lesson Details',
			message: 'This lesson has not been completed yet.'
		},
		learner: {
			icon: 'untitled-ui/time/hourglass-01',
			title: 'Awaiting Lesson Details',
			message: 'This lesson has ended. Details will be available once your instructor completes the lesson.'
		}
	}
};

@Component({
	selector: 'lb-lesson-detail-unavailable',
	standalone: true,
	imports: [MDContentBoxComponent, MDIconComponent],
	templateUrl: './lesson-detail-unavailable.component.html',
	styleUrl: './lesson-detail-unavailable.component.scss'
})
export class LessonDetailUnavailableComponent {
	private readonly state = inject(LessonDetailStateService);
	private readonly rescheduleLessonDialogService = inject(RescheduleLessonDialogService);

	userView = input<'instructor' | 'learner'>('instructor');
	/**
	 * Explicit reason, for callers whose reason isn't a lesson status — e.g. the learner view hiding a
	 * not-yet-taught lesson's details. Left unset, the reason is derived from the lesson's status.
	 */
	reason = input<LessonDetailUnavailableReason | null>(null);
	dataChanged = output<void>();

	activeReason = computed<LessonDetailUnavailableReason | null>(() => {
		const explicit = this.reason();
		if (explicit) return explicit;

		if (this.state.isAbsent()) return 'absent';
		if (this.state.isAwaitingPayment()) return 'awaiting-payment';
		if (this.state.isPendingReschedule()) return 'pending-reschedule';
		return null;
	});

	config = computed<IUnavailableConfig | null>(() => {
		const r = this.activeReason();
		if (!r) return null;
		return UNAVAILABLE_CONFIG[r][this.userView()];
	});

	/**
	 * A lesson that is only waiting — for its turn, or for the instructor's write-up — has nothing
	 * wrong with it, so it gets the plain box. Every other reason is something the reader may need
	 * to act on.
	 */
	boxType = computed<IndicatorType>(() => {
		const r = this.activeReason();
		return r === 'not-yet-taught' || r === 'awaiting-details' ? 'default' : 'warning';
	});

	// Chirp room lessons run on the room's shared schedule, so learners never reschedule them. An
	// instructor may reschedule only lessons of their own era — a predecessor's or substitute's is view-only.
	canReschedule = computed(() =>
		this.userView() === 'instructor' ? isLessonEditable(this.state.lesson()) : !this.state.isChirpRoom()
	);

	reschedule(): void {
		const lesson = this.state.lesson();
		if (!lesson) return;
		this.rescheduleLessonDialogService
			.open(lesson, this.userView())
			.pipe(take(1))
			.subscribe((result) => {
				if (result) this.dataChanged.emit();
			});
	}
}
